// The staff dashboard: one tab per area an account works in. The customer's
// own dashboard is `DASHBOARD`.

import type {
  CatalogueQualityIssue,
  CustomerActivityKind,
} from '../../dto/dashboard/staff-dashboard.res'

import { ACCOUNT } from './account'
import { CATALOGUE } from './catalogue'
import { COMMON } from './common'
import { CONTROLS } from './controls'
import { MATCHING } from './matching'
import { NAV } from './nav'
import { TERMS } from './terms'

const days = (n: number) => (n === 1 ? '1 Tag' : `${n} Tage`)

export const STAFF_DASHBOARD = {
  tabs: {
    sales: TERMS.advisor,
    projects: NAV.projects,
    catalogue: NAV.catalogue,
    system: NAV.system,
  },

  /**
   * The project half, which both dashboards that run projects render: the
   * table of what the reader works on, and the feed of what moved on it.
   *
   * Under the projects rather than under the advisor, because a project
   * manager reads every one of these too — the advisor's namespace below is
   * what only the customer half says.
   */
  projects: {
    activityHint: 'Was auf deinen Projekten passiert ist.',
    projectsSubtitle: 'Zuletzt vom Kunden bearbeitet zuerst',
    allProjects: 'Alle Projekte',
    lastCustomerActivity: 'Letzte Kundenaktivität',
    noActiveProjects: 'Keine aktiven Projekte.',
    activityKinds: {
      CONFIGURATOR: TERMS.configurator,
      DOCUMENT_UPLOAD: 'Upload',
      DOCUMENT_VIEW: 'Angesehen',
      MATCH_LIKE: MATCHING.liked,
      MATCH_DISLIKE: MATCHING.disliked,
    } satisfies Record<CustomerActivityKind, string>,
    changedAnswers: (n: number) =>
      n === 1 ? '1 Antwort geändert' : `${n} Antworten geändert`,
    matchedProperty: (location: string | null) =>
      location ? `${TERMS.property} in ${location}` : TERMS.property,
    noActivity: (n: number) =>
      `Keine Kundenaktivität in den letzten ${days(n)}.`,
    feedbackQuote: (text: string) => `„${text}“`,
  },
  /** The heading over the feed of what happened, on both dashboards that run
   *  projects. */
  activity: 'Aktivität',
  /** The window a figure or list covers. */
  lastDays: (n: number) => `Letzte ${days(n)}`,
  /** Under a list that shows only its first entries. */
  more: (n: number) => `${n} weitere`,
  loadFailed: 'Das Dashboard konnte nicht geladen werden.',
  activeProjects: 'Aktive Projekte',
  lastActive: (date: string) => `${ACCOUNT.lastActive} ${date}`,
  nextRun: (date: string) => `${CONTROLS.cronJob.nextRun} ${date}`,

  sales: {
    activeProjectsHint: (total: number) => `${total} insgesamt`,
    quiet: 'Stille Projekte',
    quietHint: (n: number) => `Ohne Kontakt seit ${days(n)}`,
    unacknowledged: 'Offene Reaktionen',
    unacknowledgedHint: 'Match-Reaktionen, noch nicht bestätigt',

    feedTitle: 'Was deine Kunden zuletzt gemacht haben',
    /** A count that stopped at what was read. */
    atLeast: (n: number) => `${n}+`,

    neverActive: 'Noch nie aktiv',
    noQuiet: 'Alle Kunden aktiver Projekte waren zuletzt da.',

    toReview: 'Treffer zu prüfen',
    matchingUnavailable: 'HubSpot ist gerade nicht erreichbar.',
    openMatching: 'Zum Matching',
  },

  catalogue: {
    measuresInConfigurator: (n: number) => `${n} im ${TERMS.configurator}`,
    productsLinked: (n: number) => `${n} verknüpft`,
    formulasUnused: (n: number) => `${n} ohne ${TERMS.usage}`,
    incomplete: 'Unvollständig',
    incompleteHint: 'Preis, Einheit, Bild oder Text fehlt',

    stalest: 'Am längsten unverändert',
    nothingStale: 'Noch nichts im Katalog.',
    stalestSubtitle: 'Kandidaten für eine Durchsicht',
    recentChanges: 'Zuletzt geändert',
    /** Whose changes the list shows. Both words agree with "Änderungen", so
     *  they read only beside that heading. */
    changeScope: { all: 'Alle', mine: 'Meine' },
    noChanges: 'Noch keine Änderungen.',
    noChangesOfMine: 'Du hast noch nichts geändert.',
    changedAt: (date: string) => `geändert ${date}`,

    mostUsed: 'Meistgenutzt',
    nothingUsed: 'Noch nichts verwendet.',
    uses: (n: number) =>
      n === 1 ? `1 ${TERMS.usage}` : `${n} ${TERMS.usagePlural}`,
    nothingUnused: 'Alles wird verwendet.',

    quality: 'Datenqualität',
    qualityIssues: {
      PRODUCT_NO_PRICE: `${TERMS.product} ohne Preis`,
      MEASURE_NO_UNIT: `${TERMS.measure} ohne Einheit`,
      PRODUCT_DISCONTINUED_LINKED: 'Eingestellt, aber noch verknüpft',
      NO_CARD_IMAGE: `Ohne ${CATALOGUE.cardImage}`,
      MEASURE_NO_SHORT_DESCRIPTION: `${TERMS.measure} ohne ${CATALOGUE.measure.shortDescription}`,
      PRODUCT_DRAFT: `${TERMS.product}-${CATALOGUE.product.draft}`,
      PRODUCT_IMPORT_GAPS: 'Import-Lücken',
    } satisfies Record<CatalogueQualityIssue, string>,
    noIssues: 'Nichts fehlt.',

    suggestedLinks: 'Wartende Verknüpfungen',
    suggestedLinksHint: (measures: number) =>
      measures === 1 ? 'Bei 1 Maßnahme' : `Bei ${measures} Maßnahmen`,
    waitingLinks: (count: number) =>
      count === 1 ? '1 Produkt wartet' : `${count} Produkte warten`,
    stalePrices: 'Produktpreise',
    stalePricesSubtitle: 'Am längsten nicht geprüft',
    neverChecked: 'Noch nie geprüft',
    noLinkedProducts: `Kein ${TERMS.product} mit Link zur Produktseite.`,
    checkedAt: (date: string) => `geprüft ${date}`,
    withoutLink: (n: number) =>
      n === 1
        ? `1 ${TERMS.product} ohne Link zur Produktseite`
        : `${n} ${TERMS.productPlural} ohne Link zur Produktseite`,
  },

  system: {
    health: 'Systemzustand',
    /** The pill on a health tile. */
    states: {
      ok: 'OK',
      check: 'Prüfen',
      failed: COMMON.error,
      off: 'Aus',
    },
    emailHint: (sent: number) =>
      `fehlgeschlagen in 24 Std. · ${sent} versendet`,
    hubspotHint: (lastSync: string | null) =>
      lastSync
        ? `fehlgeschlagene Jobs · Sync ${lastSync}`
        : 'fehlgeschlagene Jobs · Sync ist aus',
    logsHint: (warnings: number) =>
      warnings === 1 ? 'Fehler · 1 Warnung' : `Fehler · ${warnings} Warnungen`,
    imagesHint: (running: boolean, tooSmall: number) =>
      [
        running ? 'werden aufbereitet' : 'noch nicht aufbereitet',
        tooSmall > 0 ? `${tooSmall} zu kleine Bilder` : null,
      ]
        .filter(Boolean)
        .join(' · '),
    backupNone: 'Noch kein Archiv',
    backupRunning: 'Wird erstellt',

    teamActivity: 'Team-Aktivität',
    teamActivitySubtitle: 'Änderungen am Katalog',
    deletedAccount: COMMON.deletedAccount,
    changes: (n: number) => (n === 1 ? '1 Änderung' : `${n} Änderungen`),
    noTeamActivity: 'Keine Änderungen.',

    advisorLoad: 'Projekte pro Berater',
    noAdvisorLoad: 'Keine aktiven Projekte.',
    noAdvisor: `Ohne ${TERMS.advisor}`,
  },
} as const
